// pages/activities/internships/index.tsx
import {
  createInternshipColumns,
  InternshipActionType,
} from "@/colDef/internshipColDef";
import {
  Button,
  Card,
  CardContent,
  CustomAlertDialog,
  DataTable,
  ErrorMessage,
  PageContainer,
  Pagination,
  Spinner,
} from "@/components";
import InternshipDialog from "@/components/formDialog/InternshipDialog";
import Search from "@/components/Search";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useDeleteInternshipMutation,
  useInternshipsQuery,
} from "@/hooks/api/internship.queries";
import useDebounce from "@/hooks/useDebounce";
import { useAlertDialog, useFormDialog } from "@/hooks/useDialogState";
import { usePagination } from "@/hooks/usePagination";
import {
  InternshipParamsType,
  InternshipStatus,
  InternshipType,
} from "@/services/internship.service";
import { Filter, Plus } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const InternshipsPage = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<InternshipStatus | "all">(
    "all"
  );
  const navigate = useNavigate();

  const { page, limit, setPage, resetPage } = usePagination({
    initialLimit: 10,
  });
  const debouncedSearch = useDebounce(searchTerm, 400);

  const deleteDialog = useAlertDialog();
  const internshipDialog = useFormDialog({
    mode: "create" as "create" | "edit",
    internshipId: "",
    internshipData: null as InternshipType | null,
  });

  const filters: InternshipParamsType = useMemo(
    () => ({
      limit,
      page,
      ...(debouncedSearch && { search: debouncedSearch }),
      ...(statusFilter !== "all" && { status: statusFilter }),
    }),
    [debouncedSearch, statusFilter, page, limit]
  );

  const { data, isLoading, isError, refetch } = useInternshipsQuery(filters);
  const deleteMutation = useDeleteInternshipMutation(filters);

  const internships = data?.internships ?? [];
  const pagination = data?.metaData?.pagination;
  const totalEntries = pagination?.total ?? 0;
  const totalPages = pagination?.totalPages ?? 1;

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handleDelete = useCallback(() => {
    deleteMutation.mutate(deleteDialog.state.mantraId, {
      onSuccess: () => deleteDialog.close(),
    });
  }, [deleteMutation, deleteDialog]);

  const handleActionClick = useCallback(
    (payload: InternshipActionType) => {
      if (payload.action === "delete") {
        deleteDialog.open({
          message: (
            <span>
              Are you sure you want to delete the internship for{" "}
              <strong className="text-gray-800">{payload.name}</strong>? This
              action cannot be undone.
            </span>
          ),
          mantraId: payload.id,
          mantraTitle: payload.name,
          action: "delete",
        });
      } else if (payload.action === "edit") {
        const internshipData =
          internships.find((i) => i._id === payload.id) ?? null;
        internshipDialog.open({
          mode: "edit",
          internshipId: payload.id,
          internshipData,
        });
      }
    },
    [deleteDialog, internshipDialog, internships]
  );

  const columns = useMemo(
    () => createInternshipColumns(handleActionClick),
    [handleActionClick]
  );

  if (isError) {
    return (
      <ErrorMessage
        message="Failed to load internships."
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <PageContainer className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Internship Management</h1>
        <Button
          onClick={() =>
            internshipDialog.open({
              mode: "create",
              internshipId: "",
              internshipData: null,
            })
          }
          className="flex items-center gap-2 bg-gray-900 text-white hover:bg-gray-800"
        >
          <Plus size={16} />
          Add New
        </Button>
      </div>

      {/* Search + Status Filter */}
      <div className="flex items-center gap-3">
        <Search
          placeholder="Search internships..."
          value={searchTerm}
          onValueChange={(val) => {
            setSearchTerm(val);
            resetPage();
          }}
          className={{ inputGroupClass: "max-w-xs" }}
        />

        <Select
          value={statusFilter}
          onValueChange={(val) => {
            setStatusFilter(val as typeof statusFilter);
            resetPage();
          }}
        >
          <SelectTrigger className="w-36 gap-2">
            <Filter size={14} className="text-gray-500" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Showing count */}
      {!isLoading && (
        <p className="text-sm text-blue-600">
          Showing {internships.length} of {totalEntries} internships
        </p>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-16">
              <Spinner />
            </div>
          ) : (
            <DataTable<InternshipType, any>
              columns={columns}
              data={internships}
              onRowClick={(row) =>
                navigate(`/activities/internships/${row._id}`)
              }
            />
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      <Pagination
        page={page}
        totalPages={totalPages}
        totalEntries={totalEntries}
        currentCount={internships.length}
        onPageChange={setPage}
      />

      {/* Create / Edit Dialog */}
      <InternshipDialog
        mode={internshipDialog.state.mode}
        isOpen={internshipDialog.state.isOpen}
        onOpenChange={(open) => !open && internshipDialog.close()}
        filters={filters}
        internshipId={internshipDialog.state.internshipId}
        internshipData={internshipDialog.state.internshipData}
        onCancel={internshipDialog.close}
      />

      {/* Delete Confirmation */}
      <CustomAlertDialog
        isOpen={deleteDialog.state.isOpen}
        onOpenChange={(open) => !open && deleteDialog.close()}
        title="Confirm Deletion"
        description={deleteDialog.state.message}
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={deleteDialog.close}
      />
    </PageContainer>
  );
};

export default InternshipsPage;
